// src/pages/Admin/Views/Leads/SalesCallLeads.jsx
//
// Admin page for the sales team's spare calling capacity: shows real
// marketplace leads (buyer-submitted requests), masked, filtered by an
// admin-chosen set of categories + up to 5 location scopes. Deep-linkable
// from a Zoho CRM link carrying `country_code` + `category_id` + `geo_point`
// (hex-encoded EWKB, the same text form Postgres/GeoAlchemy2 use for a
// `geography` column — see utilities/geoPointUtils.js), but also works
// standalone with no query params at all — this is a general "what leads
// exist for category X at location Y" tool, not tied to any one record.
//
// `country_code` is required to resolve `category_id`: categories are
// localized per country (see src/hooks/UseCategoryData.js), so the page must
// know which country's category list to search before it can find the id —
// mirrors the `:countryCode?/:categoryId?` pattern in SalesProspectCalculator.jsx.
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Phone, Loader2, Search } from 'lucide-react';
import { Card } from '../../../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Button } from '../../../../components/ui/button';
import { useToast } from '../../../../hooks/use-toast';
import { useAdminStore } from '../../../../store/adminStore';
import { useAllCategoryBasicData } from '../../../../hooks/UseCategoryData';
import { getSalesCallLeads } from '../../../../services/AdminApis';
import { resolveCategorySelection } from '../../../../utilities/categoryGroupUtils';
import { parseHexEwkbPoint } from '../../../../utilities/geoPointUtils';
import LocaleSelector from '../../../../components/Shared/LocaleSelector';
import SalesLeadCategoryPicker from '../../../../components/Admin/Leads/SalesLeadCategoryPicker';
import SalesLeadLocationPicker, { makeRadiusLocation, DEFAULT_RADIUS_KM } from '../../../../components/Admin/Leads/SalesLeadLocationPicker';
import SalesLeadListItem from '../../../../components/Admin/Leads/SalesLeadListItem';
import SalesLeadDetailPanel from '../../../../components/Admin/Leads/SalesLeadDetailPanel';
import ScreenshotDeterrentOverlay from '../../../../components/Admin/Shared/ScreenshotDeterrentOverlay';

const PAGE_SIZE = 25;

export default function SalesCallLeads() {
  const locale = useAdminStore((state) => state.locale);
  const setLocale = useAdminStore((state) => state.setLocale);
  const { categories } = useAllCategoryBasicData(locale);
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const [categoryChips, setCategoryChips] = useState([]);
  const [includeGroup, setIncludeGroup] = useState(true);
  const [locations, setLocations] = useState([]);
  const [statusFilter, setStatusFilter] = useState('ELIGIBLE');
  const [page, setPage] = useState(1);
  const [selectedLeadId, setSelectedLeadId] = useState(searchParams.get('selected'));

  const [leads, setLeads] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [initializedFromUrl, setInitializedFromUrl] = useState(false);

  // Deep link: sync the URL's country into the store FIRST — category ids are
  // localized per country, so we can't look one up until the right country's
  // category list is loaded (same reasoning as SalesProspectCalculator.jsx).
  const urlCountryCode = searchParams.get('country_code');
  const urlLocale = urlCountryCode ? urlCountryCode.toUpperCase() : null;
  useEffect(() => {
    if (urlLocale && urlLocale !== locale) {
      setLocale(urlLocale);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlLocale]);

  // One-time initialization from the deep-link params (?country_code=&category_id=&geo_point=).
  // Waits for the store's locale to match the URL's country (if one was given) AND for
  // that country's categories to have loaded, so a slow/late category fetch can't cause
  // this to run against the wrong country's list and silently fail to find the category.
  useEffect(() => {
    if (initializedFromUrl) return;

    const urlCategoryId = searchParams.get('category_id');
    const urlGeoPoint = searchParams.get('geo_point');

    const stillWaitingForLocale = urlLocale && locale !== urlLocale;
    const stillWaitingForCategories = urlCategoryId && categories.length === 0;
    if (stillWaitingForLocale || stillWaitingForCategories) return;

    if (urlCategoryId) {
      const cat = categories.find((c) => String(c.id) === String(urlCategoryId));
      // includeGroup defaults to true, so this single chip expands to the full
      // sibling/child group via resolveCategorySelection below.
      if (cat) {
        setCategoryChips([cat]);
      } else {
        toast({
          variant: 'destructive',
          title: 'Category not found',
          description: urlLocale
            ? `Category ${urlCategoryId} was not found for ${urlLocale}. Check the link's category_id and country_code.`
            : `Category ${urlCategoryId} was not found for the current country (${locale || 'none selected'}). This link may be missing a country_code param.`,
        });
      }
    }
    if (urlGeoPoint) {
      const parsed = parseHexEwkbPoint(urlGeoPoint);
      if (parsed) {
        setLocations([makeRadiusLocation({ lat: parsed.lat, lng: parsed.lng, radiusKm: DEFAULT_RADIUS_KM, label: 'Selected location' })]);
      } else {
        toast({ variant: 'destructive', title: 'Invalid location', description: 'The geo_point in this link could not be parsed.' });
      }
    }
    setInitializedFromUrl(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, locale, urlLocale, initializedFromUrl, searchParams]);

  // Category chips reference a specific country's category rows, so a manual
  // country change (via the picker below) after the page has settled makes
  // them stale — clear them rather than silently matching zero categories.
  useEffect(() => {
    if (!initializedFromUrl) return;
    setCategoryChips([]);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  const resolvedCategoryIds = useMemo(
    () => Array.from(new Set(categoryChips.flatMap((cat) => resolveCategorySelection(categories, cat, includeGroup)))),
    [categoryChips, categories, includeGroup]
  );

  const canSearch = resolvedCategoryIds.length > 0 && locations.length > 0;

  const fetchLeads = useCallback(async () => {
    if (!canSearch) {
      setLeads([]);
      setTotalCount(0);
      return;
    }
    setLoading(true);
    try {
      const data = await getSalesCallLeads({
        categoryIds: resolvedCategoryIds,
        locations,
        statusFilter,
        page,
        limit: PAGE_SIZE,
      });
      setLeads(data.leads || []);
      setTotalCount(data.total_count || 0);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to load leads.';
      toast({ variant: 'destructive', title: 'Error', description: msg });
      setLeads([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedCategoryIds.join(','), JSON.stringify(locations), statusFilter, page, canSearch]);

  useEffect(() => {
    if (!initializedFromUrl) return;
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchLeads, initializedFromUrl]);

  // Keep the URL in the canonical, bookmarkable form once filters are established.
  useEffect(() => {
    if (!initializedFromUrl) return;
    const next = new URLSearchParams();
    if (resolvedCategoryIds.length) next.set('category_ids', JSON.stringify(resolvedCategoryIds));
    if (locations.length) next.set('locations', JSON.stringify(locations));
    if (statusFilter !== 'ELIGIBLE') next.set('status_filter', statusFilter);
    if (page !== 1) next.set('page', String(page));
    if (selectedLeadId) next.set('selected', selectedLeadId);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedCategoryIds.join(','), JSON.stringify(locations), statusFilter, page, selectedLeadId, initializedFromUrl]);

  const selectedLead = useMemo(
    () => leads.find((l) => l.lead_id === selectedLeadId) || null,
    [leads, selectedLeadId]
  );

  const isEmptyBecauseParentOnly = categoryChips.length > 0 && resolvedCategoryIds.length === 0;
  const hasMore = page * PAGE_SIZE < totalCount;

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-4">
      <div className="flex flex-col gap-1">
        <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2 text-foreground">
          <Phone className="h-5 w-5 text-brand-purple flex-shrink-0" />
          <span>Prospect Call Leads</span>
        </h1>
      </div>

      <Card className="p-4 space-y-4">
        <div>
          <h3 className="text-sm font-semibold mb-2">Country</h3>
          <LocaleSelector className="w-full max-w-xs bg-white border-gray-200" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h3 className="text-sm font-semibold mb-2">Category</h3>
            <SalesLeadCategoryPicker
              locale={locale}
              categories={categories}
              chips={categoryChips}
              onChangeChips={(chips) => { setPage(1); setCategoryChips(chips); }}
              includeGroup={includeGroup}
              onChangeIncludeGroup={(v) => { setPage(1); setIncludeGroup(v); }}
            />
          </div>
          <div>
            <h3 className="text-sm font-semibold mb-2">Location</h3>
            <SalesLeadLocationPicker
              locations={locations}
              onChangeLocations={(locs) => { setPage(1); setLocations(locs); }}
              countryCode={locale}
            />
          </div>
        </div>

        <div className="flex items-center gap-3 pt-2 border-t">
          <span className="text-sm text-muted-foreground">Status</span>
          <Select value={statusFilter} onValueChange={(v) => { setPage(1); setStatusFilter(v); }}>
            <SelectTrigger className="h-9 w-40 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ELIGIBLE">Eligible (verified)</SelectItem>
              <SelectItem value="ALL">All statuses</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {!canSearch && !isEmptyBecauseParentOnly && (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          <Search className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
          Pick at least one category and one location above to see matching leads.
        </Card>
      )}

      {canSearch && (
        <ScreenshotDeterrentOverlay>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <div className="lg:col-span-2 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{totalCount} lead{totalCount === 1 ? '' : 's'} found</span>
                {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
              </div>
              <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
                {leads.map((lead) => (
                  <SalesLeadListItem
                    key={lead.lead_id}
                    lead={lead}
                    isSelected={lead.lead_id === selectedLeadId}
                    onClick={() => setSelectedLeadId(lead.lead_id)}
                  />
                ))}
                {!loading && leads.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-8">No leads match these filters.</p>
                )}
              </div>
              {(page > 1 || hasMore) && (
                <div className="flex justify-between pt-2">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                    Previous
                  </Button>
                  <Button variant="outline" size="sm" disabled={!hasMore} onClick={() => setPage((p) => p + 1)}>
                    Next
                  </Button>
                </div>
              )}
            </div>
            <div className="lg:col-span-3">
              <SalesLeadDetailPanel lead={selectedLead} loading={false} />
            </div>
          </div>
        </ScreenshotDeterrentOverlay>
      )}
    </div>
  );
}
